import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Ripple } from "primereact/ripple";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Button } from "primereact/button";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";
import { Image } from "primereact/image";

import NoDataCreated from "../../../../../Components/NoDataCreated";
import ErrorPage from "../../../../../Components/ErrorPage";
import { goToTop } from "../../../../../Components/goToTop";
import { InputText } from "primereact/inputtext";
import DocumentViewModal from "../../../../../Components/DocumentViewModal";
import TextInput from "../../../../../Components/TextInput";
import { checkText } from "../../../../../Utils/Validator";
import { getLoginUserRoleName, UserRoleName } from "../../../../../Utils/LoginUserRoleName";

import { useAppDispatch, useAppSelector } from "../../../../../Controllers/hooks";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../Controllers/Store";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";

import {
    SiteVisit as SiteVisitType,
    SiteVisitInspectionList,
    SiteVisitBuildingInspectorResponse,
    ResponseFileList
} from "../../../../../Controllers/Types/SiteVisit/SiteVisitTypes";

import { getAllSiteVisitList } from "../../../../../Controllers/Thunks/SiteVisitThunks";

import {
    sendSiteVisitReportViaEmail,
    signSiteVisitReportBi
} from "../../../../../Api/SiteVisit/SiteVisitApiServices";

interface EmailInput {
    value: string;
    error: string;
}

const SiteVisitReport: React.FC = () => {
    const toast = useRef<Toast>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const projectData = location.state?.projectData;
    const folderId = Number(projectData.folder_id);

    const siteVisitLoading = useAppSelector((state) => state?.siteVisitList?.siteVisitLoading);
    const siteVisitError = useAppSelector((state) => state?.siteVisitList?.siteVisitError);

    const [loading, setLoading] = useState<boolean>(false);

    const [siteVisitData, setSiteVisitData] = useState<SiteVisitType | null>(null);
    const [siteInspectionData, setSiteInspectionData] = useState<SiteVisitInspectionList[]>([]);
    const [isSignedReportByBi, setIsSignedReportByBi] = useState<boolean>(false);

    const [loginUserRoleName, setLoginUserRoleName] = useState<UserRoleName | ''>('');
    const [documentFilePath, setDocumentFilePath] = useState<string>("");
    const [showNoteDocViewModal, setShowNoteDocViewModal] = useState<boolean>(false);

    const [showSignatureModal, setShowSignatureModal] = useState<boolean>(false);
    const [signature, setSignature] = useState<string>('');
    const [signatureError, setSignatureError] = useState<string>('');

    /* For send email */
    const [isSendMail, setIsSendMail] = useState<boolean>(false);
    const [showMailModal, setShowMailModal] = useState<boolean>(false);
    const [emailAddresses, setEmailAddresses] = useState<EmailInput[]>([
        { value: '', error: '' },
    ]);

    /* Download */
    const [isDownloading, setIsDownloading] = useState<boolean>(false);

    /* PERMISION HANDLER */
    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) =>
            hasPermission(state, screen, permission)
        );
    };

    const isBuildingInspector = (): boolean => {
        return (loginUserRoleName.toLocaleLowerCase() === 'building inspector' || loginUserRoleName.toLocaleLowerCase() === 'area manager' || loginUserRoleName.toLocaleLowerCase() === 'building inspector company' || loginUserRoleName.toLocaleLowerCase() === 'plan check engineer');
    };

    const havePermissionDownloadSiteVisitReport = useCanEdit("Site Visit", "dwonload_site_visit");
    const havePermissionShareSiteVisit = useCanEdit("Site Visit", "share_site_visit");
    const havePermissionAddSignature = useCanEdit("Site Visit", "add_signature");
    /*  */

    // Get logged in user role name
    useEffect(() => {
        const fetchUserRole = async () => {
            const roleName = await getLoginUserRoleName();
            setLoginUserRoleName(roleName);
        };

        fetchUserRole();
    }, []);

    // Fetch data initially from state
    useEffect(() => {
        dispatch(getAllSiteVisitList(folderId));
    }, [location?.state]);

    // get the data from state
    const siteVisitList: SiteVisitType[] = useAppSelector((state) => state?.siteVisitList?.siteVisitList);

    useEffect(() => {
        if (location.state && location.state.siteVisitData) {
            const hasData = siteVisitList && siteVisitList.length > 0;
            const stateData = location.state.siteVisitData as SiteVisitType;

            if (hasData) {
                const data = siteVisitList.filter((siteVisit) => siteVisit.id === stateData.id);
                if (data && data.length > 0) {
                    setSiteVisitData(data[0]);
                }
            }
        }
    }, [location.state, siteVisitList]);

    useEffect(() => {
        if (siteVisitData && siteVisitData !== null) {
            if (siteVisitData?.sign_report === 1) {
                setIsSignedReportByBi(true);
            } else {
                setIsSignedReportByBi(false);
            }
        } else {
            setIsSignedReportByBi(false);
        }
    }, [siteVisitData]);

    useEffect(() => {
        if (siteVisitData && siteVisitData !== null) {
            if (siteVisitData?.siteVisitInspectionList && siteVisitData?.siteVisitInspectionList?.length > 0) {
                setSiteInspectionData(siteVisitData?.siteVisitInspectionList);
            } else {
                setSiteInspectionData([]);
            }
        } else {
            setSiteInspectionData([]);
        }
    }, [siteVisitData]);


    /* *********************************** Mail ********************************** */
    const handleCloseMailModal = () => {
        setShowMailModal(false);
        setEmailAddresses([{ value: '', error: '' }]);
    }

    const handleSendEmail = () => {
        setShowMailModal(true);
    }

    const validateEmail = (email: string): string => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email) return 'Email is required.';
        if (!emailRegex.test(email)) return 'Enter a valid email.';
        return '';
    };

    // validations.
    function checkCredentialsEmail(): boolean {
        const updatedEmails = emailAddresses.map((email) => {
            const error = validateEmail(email.value);
            return { ...email, error };
        });

        setEmailAddresses(updatedEmails);

        const hasErrors = updatedEmails.some(email => email.error !== '');
        return !hasErrors;
    }

    function showAPIErrorEmail(keyName: string, error: string): void {
        if (keyName === "email_list[]") {
            const updatedEmails = emailAddresses.map((email) => ({
                ...email,
                error,
            }));
            setEmailAddresses(updatedEmails);
        }
    }

    const sendEMail = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsEmail()) return;

        setLoading(true);

        const site_visit_id: number = siteVisitData?.id!;
        const email_list: string[] = emailAddresses.map((email) => email.value);

        const data = await sendSiteVisitReportViaEmail(
            site_visit_id,
            email_list
        );

        if (data.statusCode === 200) {
            dispatch(getAllSiteVisitList(folderId));
            setLoading(false);
            handleCloseMailModal();

            if (toast.current) {
                toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Email sent successfully.",
                    life: 3000,
                });
            }
        } else if (data.statusCode === 422) {
            setLoading(false);
            Object.entries(data.errors ?? []).map(([key, value], index) =>
                showAPIErrorEmail(key, value[0])
            );
        } else {
            setLoading(false);
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error sending the email.",
                    life: 3000,
                });
            }
        }
    }

    const handleAddMail = () => {
        const lastEmail = emailAddresses[emailAddresses.length - 1];
        const validationMessage = validateEmail(lastEmail.value);

        if (validationMessage) {
            const updatedEmails = [...emailAddresses];
            updatedEmails[updatedEmails.length - 1].error = validationMessage;
            setEmailAddresses(updatedEmails);
            return;
        }

        setEmailAddresses([...emailAddresses, { value: '', error: '' }]);
    };

    const handleRemoveMail = (index: number) => {
        if (emailAddresses.length === 1) return;
        const updatedEmails = emailAddresses.filter((_, i) => i !== index);
        setEmailAddresses(updatedEmails);
    };

    const handleEmailChange = (index: number, newValue: string) => {
        const updatedEmails = [...emailAddresses];
        updatedEmails[index].value = newValue;
        updatedEmails[index].error = '';
        setEmailAddresses(updatedEmails);
    };

    const mailModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Send report via mail
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseMailModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    )

    const mailModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Cancel"
                className="custom_btn secondary"
                onClick={handleCloseMailModal}
            />
            <Button
                loading={loading}
                label={`${loading ? 'Sending' : 'Send'}`}
                className="custom_btn primary"
                onClick={sendEMail}
            />
        </div>
    )

    const handleViewReportDocument = () => {
        if (siteVisitData?.file_download_path) {
            setShowNoteDocViewModal(true);
            setDocumentFilePath(siteVisitData?.file_download_path);
        }
    };
    /* *************************************************************************** */


    /* ********************************** Signature ********************************** */
    const handleCloseSignatureModal = () => {
        setShowSignatureModal(false);
        setSignature('');

        if (isSendMail === true) {
            setIsSendMail(false);
            setEmailAddresses([{ value: '', error: '' }]);
        }
    }

    const handleAddSignature = () => {
        setShowSignatureModal(true);
        setSignature('');
    }

    function checkSignReportCredentials(): boolean {
        const isSignatureValid = checkText(signature, true, setSignatureError);

        const areEmailsValid = isSendMail ? checkCredentialsEmail() : true;

        return isSignatureValid && areEmailsValid;
    }

    function showAPIErrorSignReport(keyName: string, error: string): void {
        if (keyName === "sign") {
            setSignatureError(error);
        }

        if (keyName === "email_list[]" && isSendMail) {
            const updatedEmails = emailAddresses.map((email) => ({
                ...email,
                error,
            }));
            setEmailAddresses(updatedEmails);
        }
    }

    useEffect(() => {
        checkText(signature, false, setSignatureError)
    }, [signature]);

    const handleChangeSendMail = (e: CheckboxChangeEvent) => {
        const send = e.checked;

        if (send === true) {
            setIsSendMail(true);
        } else {
            setIsSendMail(false);
            setEmailAddresses([{ value: '', error: '' }]);
        }
    }

    const handleSignReport = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkSignReportCredentials()) {
            setLoading(true);

            const site_visit_id: number = siteVisitData?.id!;
            const sign: string = signature;
            const email_list: string[] = isSendMail ? emailAddresses?.map((email) => email.value) : [];

            const data = await signSiteVisitReportBi(
                site_visit_id,
                sign,
                email_list
            );

            if (data.statusCode === 200) {
                dispatch(getAllSiteVisitList(folderId));
                handleCloseSignatureModal();
                setLoading(false);

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: `The site visit report has been signed ${isSendMail ? 'and shared via email' : ''} successfully.`,
                        life: 3000,
                    });
                }
            } else if (data.statusCode === 422) {
                setLoading(false);

                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorSignReport(key, value[0])
                );
            } else {
                setLoading(false);
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'An error occurred while signing the site visit report.',
                        life: 3000
                    });
                }
            }
        }
    }

    const signatureModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Add signature
            </h5>

            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseSignatureModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    )

    const signatureModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Cancel"
                className="custom_btn secondary"
                onClick={handleCloseSignatureModal}
            />
            <Button
                loading={loading}
                label={`${loading ? 'Processing' : 'Submit'}`}
                className="custom_btn primary"
                onClick={handleSignReport}
            />
        </div>
    )
    /* ******************************************************************************* */


    const handleCloseFileViewModal = () => {
        setShowNoteDocViewModal(false);
    };

    const handleViewNoteDocument = (filePath: string) => {
        if (filePath && filePath !== "") {
            setShowNoteDocViewModal(true);
            setDocumentFilePath(filePath);
        } else {
            setDocumentFilePath("");
        }
    };


    /* ******************************** Download ********************************* */
    const handleDownloadReport = async () => {
        if (isDownloading) return;
        // const reportPath = baseURL +"/"+ siteVisitData?.file_download_path;
        const reportPath = siteVisitData?.file_download_path;
        if (!reportPath) {
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "File not found!",
                    life: 3000,
                });
            }
            return;
        }

        setIsDownloading(true);

        setTimeout(async () => {
            try {
                const response = await fetch(reportPath);

                if (!response.ok) {
                    throw new Error("Failed to fetch the file.");
                }

                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = reportPath.split("/").pop() || "Site visit report.pdf";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(blobUrl);

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "File downloaded successfully.",
                        life: 3000,
                    });
                }
            } catch (error) {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "Error downloading the file",
                        life: 3000,
                    });
                }
            } finally {
                setIsDownloading(false);
            }
        }, 500);
    };
    /* *************************************************************************** */


    /* **************************** Common functions ***************************** */
    const goBack = () => {
        navigate(-1);
        goToTop();
    };
    /* *************************************************************************** */

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content full_screen_view">
                {siteVisitLoading ? (
                    <div className="loading_container">
                        <ProgressSpinner className="progresscircle custom" style={{ width: '50px', height: '50px', stroke: '#8953D7' }} strokeWidth="8" fill="var(--surface-ground)" animationDuration="1s" />
                    </div>
                ) : siteVisitError ? (
                    <ErrorPage errorMessage={siteVisitError} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-3)}>
                                    {projectData?.job_number}: {projectData?.address}
                                </li>
                                <li className="page_breadcrumb_item has_child">
                                    {projectData?.folder}
                                </li>
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-2)}>
                                    {projectData?.feature_name}
                                </li>

                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    Site visit detail
                                </li>

                                <li className="page_breadcrumb_item active">Site visit {siteVisitData?.site_visit_no} - Report</li>
                            </ul>

                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>
                        </div>

                        <div className="page_header_area justify-content-end mt-sm-3 mt-2">
                            <div className="page_action_area">
                                {siteVisitData?.file_download_path !== null && (
                                    <React.Fragment>
                                        {havePermissionDownloadSiteVisitReport && (
                                            <button
                                                className="add_data_link_btn p-ripple"
                                                onClick={handleDownloadReport}
                                                disabled={isDownloading}
                                            >
                                                {isDownloading ? (
                                                    <ProgressSpinner
                                                        style={{ width: "20px", height: "20px" }}
                                                        strokeWidth="5"
                                                        fill="none"
                                                        animationDuration="1s"
                                                        className="spin_loader"
                                                    />
                                                ) : (
                                                    <i className="bi bi-download"></i>
                                                )}
                                                <span>{isDownloading ? "Downloading" : "Download"}</span>
                                                <Ripple />
                                            </button>
                                        )}

                                        {havePermissionShareSiteVisit && (
                                            <button
                                                className="add_data_link_btn p-ripple"
                                                onClick={handleSendEmail}
                                            >
                                                <i className="bi bi-envelope"></i>
                                                <span>Send</span>
                                                <Ripple />
                                            </button>
                                        )}
                                    </React.Fragment>
                                )}

                                {isBuildingInspector() && isSignedReportByBi === false && (
                                    <React.Fragment>
                                        {havePermissionAddSignature && (
                                            <button
                                                className="add_data_link_btn p-ripple"
                                                onClick={handleAddSignature}
                                            >
                                                <i className="bi bi-pen"></i>
                                                <span>Sign Report</span>
                                                <Ripple />
                                            </button>
                                        )}
                                    </React.Fragment>
                                )}
                            </div>
                        </div>

                        {siteVisitData && siteVisitData !== null ? (
                            <article className="site_visit_report_container">
                                <div className="sv_report_body">
                                    <div className="site_visit_report_detail_area">
                                        <div className="row">
                                            <div className="col-12 col-sm-6">
                                                <div className="site_visit_report_detail_sub">
                                                    <div className="table-responsive">
                                                        <table className="table table-borderless">
                                                            <thead>
                                                                <tr>
                                                                    <th colSpan={2}>Project Overview</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr>
                                                                    <td className="text_bold">Site Address:</td>
                                                                    <td className="text-capitalize">
                                                                        {siteVisitData?.project_data ? (
                                                                            <span>
                                                                                {siteVisitData?.project_data?.address || '----'}&nbsp;
                                                                            </span>
                                                                        ) : (
                                                                            <span>----</span>
                                                                        )}
                                                                    </td>
                                                                </tr>

                                                                <tr>
                                                                    <td className="text_bold">Description:</td>
                                                                    <td className="first_letter_capitalized">
                                                                        {siteVisitData?.project_data ? (
                                                                            <span>
                                                                                {siteVisitData?.project_data?.description || '----'}&nbsp;
                                                                            </span>
                                                                        ) : (
                                                                            <span>----</span>
                                                                        )}
                                                                    </td>
                                                                </tr>

                                                                <tr>
                                                                    <td className="text_bold">Building inspector:</td>
                                                                    <td className="text-capitalize">
                                                                        {siteVisitData?.building_inspector_data ? (
                                                                            <span>
                                                                                {siteVisitData?.building_inspector_data?.name || '----'}
                                                                                {siteVisitData?.building_inspector_data?.company_name ? (' - ' + siteVisitData?.building_inspector_data?.company_name) : ''}
                                                                            </span>
                                                                        ) : (
                                                                            <span>----</span>
                                                                        )}
                                                                    </td>
                                                                </tr>

                                                                <tr>
                                                                    <td className="text_bold">Architecture:</td>
                                                                    <td className="text-capitalize">
                                                                        {siteVisitData?.architecture_data ? (
                                                                            <span>
                                                                                {siteVisitData?.architecture_data?.name || '----'}
                                                                                {siteVisitData?.architecture_data?.company_name ? (' - ' + siteVisitData?.architecture_data?.company_name) : ''}
                                                                            </span>
                                                                        ) : (
                                                                            <span>----</span>
                                                                        )}
                                                                    </td>
                                                                </tr>

                                                                <tr>
                                                                    <td className="text_bold">Client Name:</td>
                                                                    <td className="text-capitalize">
                                                                        {siteVisitData?.client_name || '----'}
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-12 col-sm-6">
                                                <div className="site_visit_report_detail_sub">
                                                    <div className="table-responsive">
                                                        <table className="table table-borderless">
                                                            <thead>
                                                                <tr>
                                                                    <th colSpan={2}>Site Visit Details</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr>
                                                                    <td className="text_bold">Site visit no.:</td>
                                                                    <td>Site visit {siteVisitData?.site_visit_no}</td>
                                                                </tr>

                                                                <tr>
                                                                    <td className="text_bold">Date:</td>
                                                                    <td>{siteVisitData?.date || '----'}</td>
                                                                </tr>

                                                                <tr>
                                                                    <td className="text_bold">Visit Conducted:</td>
                                                                    <td className="text-capitalize">
                                                                        {siteVisitData?.building_inspector_data ? (
                                                                            <span>
                                                                                {siteVisitData?.building_inspector_data?.name || '----'}
                                                                            </span>
                                                                        ) : (
                                                                            <span>----</span>
                                                                        )}
                                                                    </td>
                                                                </tr>

                                                                <tr>
                                                                    <td className="text_bold">Client Name:</td>
                                                                    <td className="text-capitalize">
                                                                        {siteVisitData?.client_name || '----'}
                                                                    </td>
                                                                </tr>

                                                                <tr>
                                                                    <td className="text_bold">Description:</td>
                                                                    <td className="first_letter_capitalized">{siteVisitData?.note || '----'}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="status_lable_container">
                                        <div className="status_lable_area p-0 my-3">
                                            <div className="status_lable">
                                                <span style={{ backgroundColor: "#FFEFBC" }}></span>
                                                <h6>Unsatisfactory</h6>
                                            </div>
                                            <div className="status_lable">
                                                <span style={{ backgroundColor: "#B9F6CA" }}></span>
                                                <h6>Satisfactory</h6>
                                            </div>
                                        </div>
                                    </div>

                                    {siteInspectionData && siteInspectionData?.length > 0 ? (
                                        <div className="sv_report_table_area table-responsive pl_table_area">
                                            <table className="table table-bordered sv_report_table pl_table mb-0">
                                                <thead>
                                                    <tr className="sv_report_table_header light">
                                                        <th style={{ width: "5%" }} className="item_code_column">
                                                            Note no.
                                                        </th>

                                                        <th style={{ width: "80%" }} className="comment_column">
                                                            Note
                                                        </th>

                                                        <th style={{ width: "15%" }} className="files_column">
                                                            Files
                                                        </th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {siteInspectionData?.map((inspection) => {
                                                        return (
                                                            <React.Fragment>
                                                                <tr key={inspection?.id} className="sv_report_table_data data_group_row">
                                                                    <td colSpan={3}>
                                                                        {inspection?.inspection_name}
                                                                    </td>
                                                                </tr>

                                                                {inspection?.note_list && inspection?.note_list?.length > 0 && (
                                                                    <React.Fragment>
                                                                        {inspection?.note_list?.map((note) => {
                                                                            const biResponse: SiteVisitBuildingInspectorResponse | null = note?.response_building_inspector || null;
                                                                            const biResponseFiles: ResponseFileList[] = note?.response_building_inspector?.file_list || [];

                                                                            return (
                                                                                <tr
                                                                                    key={note?.id}
                                                                                    className="sv_report_table_data inner_tabel_row"
                                                                                    style={{ backgroundColor: note?.status === "Unsatisfactory" ? "#FFEFBC" : "#B9F6CA" }}
                                                                                >
                                                                                    <td style={{ width: "5%" }} className="item_code_column">
                                                                                        {note?.note_no}.
                                                                                    </td>

                                                                                    <td style={{ width: "80%" }} className="comment_column first_letter_capitalized">
                                                                                        {note?.note}
                                                                                    </td>

                                                                                    <td style={{ width: "15%" }} className="files_column">
                                                                                        <div className="item_file_area mt-1">
                                                                                            {biResponse && biResponseFiles && biResponseFiles?.length > 0 ? (
                                                                                                <React.Fragment>
                                                                                                    {biResponseFiles?.map((file) => {
                                                                                                        return (
                                                                                                            <React.Fragment>
                                                                                                                {file?.extension === "pdf" ? (
                                                                                                                    <div className="item_file_label">
                                                                                                                        <div
                                                                                                                            className="item_doc p-ripple"
                                                                                                                            onClick={() => handleViewNoteDocument(file?.path!)}
                                                                                                                        >
                                                                                                                            <i className="bi bi-file-earmark-pdf"></i>
                                                                                                                        </div>
                                                                                                                    </div>
                                                                                                                ) : (
                                                                                                                    <div className="item_file_label">
                                                                                                                        <Image
                                                                                                                            src={`${file?.path}`}
                                                                                                                            alt={`Image`}
                                                                                                                            className="item_image"
                                                                                                                            preview
                                                                                                                        />
                                                                                                                    </div>
                                                                                                                )}
                                                                                                            </React.Fragment>
                                                                                                        );
                                                                                                    }
                                                                                                    )}
                                                                                                </React.Fragment>
                                                                                            ) : (
                                                                                                <div className="no_file_found">
                                                                                                    ----
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    </td>
                                                                                </tr>
                                                                            )
                                                                        })}
                                                                    </React.Fragment>
                                                                )}
                                                            </React.Fragment>
                                                        )
                                                    })}
                                                </tbody>

                                            </table>
                                        </div>
                                    ) : (
                                        <div className="no_report_data">
                                            <h6>
                                                <i className="bi bi-exclamation-circle me-1"></i>
                                                No data found!
                                            </h6>
                                        </div>
                                    )}

                                    {isSignedReportByBi && (
                                        <div className="sv_report_footer">
                                            <div className="sv_report_footer_item">
                                                <h3>
                                                    {siteVisitData?.sign}
                                                </h3>

                                                <h5>Building Inspector</h5>
                                                <h6>{siteVisitData?.sign_date}</h6>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </article>
                        ) : (
                            <div className="page_main_content_section no_header">
                                <NoDataCreated
                                    message={`No data found!`}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>


            {/* Signature modal */}
            <Dialog
                header={signatureModalHeader}
                footer={signatureModalFooter}
                headerClassName="custom_modal_header"
                visible={showSignatureModal}
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseSignatureModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-3 pb-sm-0 w-100">
                        <div className="row">
                            <div className="col-12">
                                <div className="page_form_group">
                                    <label htmlFor="signature" className="custom_form_label is_required">Signature</label>

                                    <InputText
                                        id='signature'
                                        value={signature}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSignature(e.target.value)}
                                        className="custom_form_input signature_input"
                                        placeholder='Enter your signature'
                                        autoFocus
                                        type='text'
                                    />

                                    {signatureError && (<small className="form_error_msg">{signatureError}</small>)}
                                </div>
                            </div>

                            <div className="col-12">
                                <div className="page_form_group">
                                    <div className="response_state">
                                        <Checkbox
                                            inputId="isSendMail"
                                            name="isSendMail"
                                            onChange={(e: CheckboxChangeEvent) => handleChangeSendMail(e)}
                                            checked={isSendMail}
                                        />
                                        <label htmlFor="isSendMail">Send the report via email as well?</label>
                                    </div>
                                </div>
                            </div>

                            {isSendMail && (
                                <div className="col-12">
                                    {emailAddresses.map((email, index) => (
                                        <div className="mail_input_area d-flex align-items-center mb-2" key={index}>
                                            <TextInput
                                                id={`emailAddress-${index}`}
                                                label={`E-mail address ${index + 1}`}
                                                labelHtmlFor={`emailAddress-${index}`}
                                                required={true}
                                                value={email.value}
                                                placeholder="abcd@gmail.com"
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                    handleEmailChange(index, e.target.value)
                                                }
                                                error={email.error}
                                                keyFilter={'email'}
                                                formGroupClassName="mb-1"
                                            />
                                            {emailAddresses.length > 1 && (
                                                <i
                                                    className="bi bi-dash-circle mail_remove_btn"
                                                    title="Remove"
                                                    onClick={() => handleRemoveMail(index)}
                                                ></i>
                                            )}
                                        </div>
                                    ))}

                                    <button className={`add_data_btn p-ripple`} onClick={handleAddMail}>
                                        <i className={`bi bi-plus-circle`}></i>
                                        Add mail
                                        < Ripple />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Mail modal */}
            <Dialog
                header={mailModalHeader}
                footer={mailModalFooter}
                headerClassName="custom_modal_header"
                visible={showMailModal}
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseMailModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-3 pb-sm-0 w-100">
                        <div className="row">
                            <div className="col-12">
                                {emailAddresses.map((email, index) => (
                                    <div className="mail_input_area d-flex align-items-center mb-2" key={index}>
                                        <TextInput
                                            id={`emailAddress-${index}`}
                                            label={`E-mail address ${index + 1}`}
                                            labelHtmlFor={`emailAddress-${index}`}
                                            required={true}
                                            value={email.value}
                                            placeholder="abcd@gmail.com"
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                handleEmailChange(index, e.target.value)
                                            }
                                            error={email.error}
                                            keyFilter={'email'}
                                            formGroupClassName="mb-1"
                                        />
                                        {emailAddresses.length > 1 && (
                                            <i
                                                className="bi bi-dash-circle mail_remove_btn"
                                                title="Remove"
                                                onClick={() => handleRemoveMail(index)}
                                            ></i>
                                        )}
                                    </div>
                                ))}

                                <button className={`add_data_btn p-ripple`} onClick={handleAddMail}>
                                    <i className={`bi bi-plus-circle`}></i>
                                    Add mail
                                    < Ripple />
                                </button>
                            </div>

                            <div className="col-12 mt-3">
                                {siteVisitData?.file_download_path !== null && (
                                    <div
                                        className="file_attached_area"
                                        onClick={handleViewReportDocument}
                                    >
                                        <i className="bi bi-paperclip"></i>
                                        <h6>Site vist report.pdf</h6>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Document view modal */}
            <DocumentViewModal
                isOpen={showNoteDocViewModal}
                onClose={handleCloseFileViewModal}
                fileUrl={documentFilePath}
            />
            {/*  */}
        </React.Fragment >
    )
}

export default SiteVisitReport;